import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import { TestId } from "../../constants/testIds";
import type { City } from "../../types/city";
import type { OperationsTeam } from "../../types/operationsTeam";
import {
  emptyStandbyOrderFormValues,
  type StandbyOrderFormValues,
  validateStandbyOrderForm,
} from "../../types/standbyOrder";
import type { Truck } from "../../types/truck";
import { type User, UserRole } from "../../types/user";
import { formatLicensePlateInput } from "../../utils/formatLicensePlate";
import {
  findEntityById,
  getEntityOptionKey,
  isEntityOptionEqual,
} from "../../utils/entityAutocomplete";
import {
  formatDepartureDateString,
  isCompleteDepartureDate,
  parseDepartureDateString,
} from "../../utils/orderDepartureDateTime";
import DetailSectionCard from "../common/DetailSectionCard";
import RupiahAmountField from "../common/RupiahAmountField";
import {
  buildRevisionExpenseKeySet,
  expenseRevisionFieldSx,
  isAdditionalExpenseRevisionKey,
  isSingletonExpenseRevisionKey,
} from "../../utils/expenseRevisionFieldStyle";

type TranslationKeys = keyof typeof translations.en;

export const STANDBY_ORDER_FORM_ID = "standby-order-form";

export interface StandbyOrderFormMeta {
  createdAtLabel?: string;
  createdByLabel?: string;
  reportReadableId?: string;
  reportUrl?: string;
}

interface StandbyOrderFormProps {
  initialValues?: StandbyOrderFormValues;
  teams: OperationsTeam[];
  cities: City[];
  trucks: Truck[];
  users: User[];
  isEdit?: boolean;
  canSelectTeam?: boolean;
  loading?: boolean;
  error?: string | null;
  meta?: StandbyOrderFormMeta;
  revisionExpenseKeys?: string[];
  additionalExpenseIds?: string[];
  hideActions?: boolean;
  onSubmit: (values: StandbyOrderFormValues) => Promise<void>;
  onCancel: () => void;
}

const ReadOnlyField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }} component="div">
      {value}
    </Typography>
  </Box>
);

const StandbyOrderForm: React.FC<StandbyOrderFormProps> = ({
  initialValues,
  teams,
  cities,
  trucks,
  users,
  isEdit = false,
  canSelectTeam = false,
  loading = false,
  error = null,
  meta,
  revisionExpenseKeys,
  additionalExpenseIds = [],
  hideActions = false,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const revisionKeys = useMemo(
    () => buildRevisionExpenseKeySet(revisionExpenseKeys),
    [revisionExpenseKeys],
  );
  const [values, setValues] = useState<StandbyOrderFormValues>(
    initialValues ?? emptyStandbyOrderFormValues(),
  );
  const [datePickerValue, setDatePickerValue] = useState<Date | null>(() =>
    parseDepartureDateString(initialValues?.date || ""),
  );
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!initialValues) {
      return;
    }
    setValues(initialValues);
    setDatePickerValue(parseDepartureDateString(initialValues.date));
  }, [initialValues]);

  const drivers = useMemo(
    () => users.filter((user) => user.role === UserRole.DRIVER && !user.deactivated),
    [users],
  );

  const assignedTeam = useMemo(
    () => teams.find((team) => team._id === values.teamOperationsId) || null,
    [teams, values.teamOperationsId],
  );

  const updateValues = (patch: Partial<StandbyOrderFormValues>) => {
    setValues((current) => ({ ...current, ...patch }));
  };

  const handleAdditionalChange = (index: number, field: "amount" | "notes", value: string) => {
    setValues((prev) => ({
      ...prev,
      expenseAdditional: prev.expenseAdditional.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    }));
  };

  const handleAddAdditionalRow = () => {
    setValues((prev) => ({
      ...prev,
      expenseAdditional: [...prev.expenseAdditional, { amount: "", notes: "" }],
    }));
  };

  const handleRemoveAdditionalRow = (index: number) => {
    setValues((prev) => ({
      ...prev,
      expenseAdditional: prev.expenseAdditional.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (datePickerValue && !isCompleteDepartureDate(datePickerValue)) {
      setValidationErrors(["standby.validation.dateInvalid"]);
      return;
    }
    const errors = validateStandbyOrderForm(values, {
      requireTeam: canSelectTeam && !isEdit,
    });
    setValidationErrors(errors);
    if (errors.length > 0) {
      return;
    }
    await onSubmit(values);
  };

  const teamField = (
    <Box data-testid={TestId.STANDBY.TEAM}>
      {canSelectTeam ? (
        <FormControl fullWidth>
          <Autocomplete
            disablePortal
            options={teams}
            value={assignedTeam}
            onChange={(_, team) => updateValues({ teamOperationsId: team?._id || "" })}
            getOptionLabel={(team) => team.name}
            disabled={isEdit}
            renderInput={(params) => <TextField {...params} label={t("Team" as TranslationKeys)} />}
          />
        </FormControl>
      ) : (
        <TextField
          label={t("Team" as TranslationKeys)}
          value={assignedTeam?.name || ""}
          disabled
          fullWidth
        />
      )}
    </Box>
  );

  const actionButtons = (
    <>
      <Button
        variant="outlined"
        onClick={onCancel}
        disabled={loading}
        data-testid={TestId.STANDBY.CANCEL}
        sx={{ textTransform: "none", fontWeight: 600 }}
      >
        {t("Cancel" as TranslationKeys)}
      </Button>
      <Button
        type="submit"
        form={STANDBY_ORDER_FORM_ID}
        variant="contained"
        disabled={loading}
        data-testid={TestId.STANDBY.SAVE}
        sx={{ textTransform: "none", fontWeight: 600 }}
      >
        {t("Save" as TranslationKeys)}
      </Button>
    </>
  );

  return (
    <Box
      component="form"
      id={STANDBY_ORDER_FORM_ID}
      onSubmit={handleSubmit}
      data-testid={TestId.STANDBY.FORM}
      sx={{ pb: 4 }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((validationError) => {
            const [key, index] = validationError.split(":");
            const label = t(key as TranslationKeys);
            return (
              <Typography key={validationError} variant="body2">
                {index ? `${label} (${Number(index) + 1})` : label}
              </Typography>
            );
          })}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          mb: 2,
        }}
      >
        <DetailSectionCard
          title={t("orders.generalSection" as TranslationKeys)}
          icon={<AssignmentIndOutlinedIcon fontSize="small" />}
          iconColor="#7C3AED"
        >
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            }}
          >
            {teamField}
            {isEdit && (
              <>
                <ReadOnlyField
                  label={t("orders.createdAt" as TranslationKeys)}
                  value={meta?.createdAtLabel || "—"}
                />
                <ReadOnlyField
                  label={t("orders.createdBy" as TranslationKeys)}
                  value={meta?.createdByLabel || "—"}
                />
                <ReadOnlyField
                  label={t("orders.report" as TranslationKeys)}
                  value={
                    meta?.reportUrl && meta.reportReadableId ? (
                      <Link
                        component={RouterLink}
                        to={meta.reportUrl}
                        underline="hover"
                        data-testid={TestId.STANDBY.VIEW_REPORT}
                        sx={{ fontWeight: 600 }}
                      >
                        {meta.reportReadableId}
                      </Link>
                    ) : (
                      "—"
                    )
                  }
                />
              </>
            )}
            <TextField
              label={t("orders.note" as TranslationKeys)}
              value={values.note}
              onChange={(event) => updateValues({ note: event.target.value })}
              multiline
              minRows={3}
              fullWidth
              sx={{ gridColumn: { sm: "1 / -1" } }}
              inputProps={{ "data-testid": TestId.STANDBY.NOTE }}
            />
          </Box>
        </DetailSectionCard>

        <DetailSectionCard
          title={t("orders.vehicleAndRouteSection" as TranslationKeys)}
          icon={<DirectionsCarFilledOutlinedIcon fontSize="small" />}
          iconColor="#16A34A"
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              }}
            >
              <Box data-testid={TestId.STANDBY.TRUCK}>
                <Autocomplete
                  disablePortal
                  options={trucks}
                  value={findEntityById(trucks, values.truckId)}
                  onChange={(_, truck) => updateValues({ truckId: truck?._id || "" })}
                  getOptionLabel={(truck) => formatLicensePlateInput(truck.licensePlate)}
                  getOptionKey={getEntityOptionKey}
                  isOptionEqualToValue={isEntityOptionEqual}
                  renderInput={(params) => (
                    <TextField {...params} label={t("License Plate" as TranslationKeys)} />
                  )}
                />
              </Box>

              <Box data-testid={TestId.STANDBY.DRIVER}>
                <Autocomplete
                  disablePortal
                  options={drivers}
                  value={findEntityById(drivers, values.driverId)}
                  onChange={(_, driver) => updateValues({ driverId: driver?._id || "" })}
                  getOptionLabel={(driver) => driver.name}
                  getOptionKey={getEntityOptionKey}
                  isOptionEqualToValue={isEntityOptionEqual}
                  renderInput={(params) => (
                    <TextField {...params} label={t("DRIVER" as TranslationKeys)} />
                  )}
                />
              </Box>

              <Box sx={{ gridColumn: { sm: "1 / -1" } }} data-testid={TestId.STANDBY.LOCATION}>
                <Autocomplete
                  disablePortal
                  options={cities}
                  value={findEntityById(cities, values.locationId)}
                  onChange={(_, city) => updateValues({ locationId: city?._id || "" })}
                  getOptionLabel={(city) => city.name}
                  getOptionKey={getEntityOptionKey}
                  isOptionEqualToValue={isEntityOptionEqual}
                  renderInput={(params) => (
                    <TextField {...params} label={t("Location" as TranslationKeys)} />
                  )}
                />
              </Box>

              <DatePicker
                label={t("standby.date" as TranslationKeys)}
                format="dd/MM/yyyy"
                value={datePickerValue}
                onChange={(newValue) => {
                  setDatePickerValue(newValue);
                  if (!newValue) {
                    updateValues({ date: "" });
                    return;
                  }
                  if (isCompleteDepartureDate(newValue)) {
                    updateValues({ date: formatDepartureDateString(newValue) });
                  }
                }}
                slotProps={{
                  textField: {
                    inputProps: { "data-testid": TestId.STANDBY.DATE },
                  },
                  field: { clearable: true },
                }}
              />
            </Box>
          </LocalizationProvider>
        </DetailSectionCard>
      </Box>

      <DetailSectionCard
        title={t("orders.expensesSection" as TranslationKeys)}
        icon={<PaymentsOutlinedIcon fontSize="small" />}
        iconColor="#2563EB"
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("orders.expensesFormHint" as TranslationKeys)}
        </Typography>

        <RupiahAmountField
          required
          fullWidth
          label={t("standby.expense" as TranslationKeys)}
          value={values.expenseStandby}
          onValueChange={(value) => updateValues({ expenseStandby: value })}
          inputProps={{ "data-testid": TestId.STANDBY.AMOUNT }}
          disabled={loading}
          sx={
            isSingletonExpenseRevisionKey(revisionKeys, "standby")
              ? expenseRevisionFieldSx
              : undefined
          }
        />

        <Divider sx={{ my: 2.5 }} />

        <Typography
          variant="subtitle2"
          sx={{ mb: 1, fontWeight: 700 }}
          data-testid={TestId.STANDBY.EXPENSE_ADDITIONAL_HEADER}
        >
          {t("orders.additionalExpenses" as TranslationKeys)}
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddAdditionalRow}
          data-testid={TestId.STANDBY.EXPENSE_ADDITIONAL_ADD}
          sx={{ mb: 2, textTransform: "none" }}
        >
          {t("orders.addAdditionalExpense" as TranslationKeys)}
        </Button>

        {values.expenseAdditional.map((row, index) => {
          const needsRevisionHighlight = isAdditionalExpenseRevisionKey(
            revisionKeys,
            additionalExpenseIds[index],
          );

          return (
            <Box
              key={`additional-${index}`}
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", sm: "1fr 2fr auto" },
                mb: 1,
                alignItems: "center",
              }}
              data-testid={TestId.STANDBY.EXPENSE_ADDITIONAL_ROW}
            >
              <RupiahAmountField
                label={t("orders.additionalAmount" as TranslationKeys)}
                value={row.amount}
                onValueChange={(value) => handleAdditionalChange(index, "amount", value)}
                inputProps={{ "data-testid": TestId.STANDBY.EXPENSE_ADDITIONAL_AMOUNT }}
                sx={needsRevisionHighlight ? expenseRevisionFieldSx : undefined}
              />
              <TextField
                label={t("Notes" as TranslationKeys)}
                value={row.notes}
                onChange={(event) => handleAdditionalChange(index, "notes", event.target.value)}
                inputProps={{ "data-testid": TestId.STANDBY.EXPENSE_ADDITIONAL_NOTES }}
                sx={needsRevisionHighlight ? expenseRevisionFieldSx : undefined}
              />
              <IconButton
                aria-label={t("Delete" as TranslationKeys)}
                onClick={() => handleRemoveAdditionalRow(index)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          );
        })}
      </DetailSectionCard>

      {!hideActions && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, flexWrap: "wrap" }}>
          {actionButtons}
        </Box>
      )}
    </Box>
  );
};

export default StandbyOrderForm;
